import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Grid } from "@mui/material";
import { GridCloseIcon } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AsyncSelect from "../../../../components/AsyncSelect";
import AsyncSelectScroll from "../../../../components/AsyncSelectScroll";
import CustomSelectScroll from "../../../../components/CustomSelectScroll";
import Flex from "../../../../components/Flex";
import Text from "../../../../components/Text";
import { PROVIDER_PATHS } from "../../../../constants/paths";
import { useGetPrEligibleClaims } from "../../../../queries/billings/claims/use-get-pr-eligible-claims";
import { useAppointmentsV2 } from "../../../../queries/patient-portal/forms/appointment/use-appointment-list-v2";
import { usePatientsList } from "../../../../queries/use-get-patients";
import {
  formatDate,
  formatDateTime,
} from "../../../../utils/date-time-utility";
import {
  DEFAULT_USER_SRC,
  DEF_PAGINATION,
  DGT_Pagination,
  getFormattedId,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../utils/utils";
import {
  DEFAULT_VISIT_BILLED_STATUS,
  INVOICE_TYPE,
  INVOICE_TYPE_VISIT_BILLED_STATUS,
  InvoiceType,
} from "./billing-constant";
import { getStoredRealm } from "@/utils/auth-storage";

// const CommonBillDetails = ({ data }: { data: any[] }) => (
//   <Flex gap={1} flexDirection={"row"}>
//     {data?.map((item) => (
//       <Flex key={item.label} gap={1} alignItems={"center"} fontSize={"18px"}>
//         {item.icon}
//         {item?.label?.length >= item?.trimmed ? (
//           <Text sx={{ overflowWrap: "break-word" }}>
//             <Tooltip title={item?.label ?? ""} placement="top">
//               {(item?.label?.slice(0, item.trimmed) + "...") as any}
//             </Tooltip>
//           </Text>
//         ) : (
//           <Text sx={{ overflowWrap: "break-word" }}>
//             {!!item.label && item.label !== "" ? item.label : "-"}
//           </Text>
//         )}
//       </Flex>
//     ))}
//   </Flex>
// );

interface SuperBillHeaderProps {
  patientValidationFormik?: any;
  patientsData?: any;
  viewBill?: boolean;
  title?: string;
  // ECRH-5347: Visit is mandatory when an invoice type was selected up front.
  isVisitRequired?: boolean;
  // Invoice type picked from the "Create Invoice" dropdown; the Select Claim
  // field is shown only for Patient Responsibility (PR).
  invoiceType?: string;
}

const SuperBillHeader = ({
  patientValidationFormik,
  patientsData,
  viewBill = true,
  title,
  isVisitRequired = false,
  invoiceType,
}: SuperBillHeaderProps) => {
  const navigate = useNavigate();
  const xTENANTID = getStoredRealm() ?? "";
  const [searchKey, setSearchKey] = useState<string>("");
  // ECRH-5347: search string for the Select Visit dropdown so the user can
  // look up a visit by its Visit ID (backend filters the appointment list on
  // this key).
  const [visitSearchKey, setVisitSearchKey] = useState<string>("");
  const location = useLocation();
  const isUpdateRoute = location.pathname;
  const isAddInvoiceRoute = isUpdateRoute.split("/")[4] === "add-invoice";
  const isPRInvoice = invoiceType === INVOICE_TYPE.PR;
  // Select Claim is a Patient Responsibility concern only, and only on the
  // manual "new invoice" flow.
  const showSelectClaim = isAddInvoiceRoute && isPRInvoice;
  // Every invoice type selects the visit first — the claim hangs off the visit,
  // so picking the claim first is what let the invoice show a visit that
  // disagreed with the claim (Visit -> Claim -> Invoice is strict).
  const showSelectVisit = isAddInvoiceRoute;
  const selectedPatientId = patientValidationFormik?.values?.patient;
  const selectedVisitId = patientValidationFormik?.values?.encounter?.value;

  const [patientPagination, setPatientPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [visitPagination, setVisitPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: patientList, isFetching: isFetchingPatients } = usePatientsList(
    {
      pagination: patientPagination,
      searchKey,
      statusKey: undefined,
      providerGroupId: xTENANTID,
    }
  );

  // Completed visits for the selected patient (same source as Create Claim).
  const {
    data: patientEncounterList,
    isLoading: isLoadingEncounterList,
    isFetching: isFetchingEncounterList,
  } = useAppointmentsV2({
    pagination: visitPagination,
    providerGroupId: getXtenantId(),
    appointmentStatus: ["COMPLETED"],
    patientUuid: selectedPatientId ? [String(selectedPatientId)] : undefined,
    // ECRH-5445: only visits that can still be invoiced should be selectable
    // here (comma-joined, same convention as the Ready For Billing filter).
    // Which statuses qualify depends on the invoice type: Self Pay / Out of
    // Network need a visit that is not fully billed yet, while Patient
    // Responsibility needs one that was fully billed to the payer.
    billedStatus:
      INVOICE_TYPE_VISIT_BILLED_STATUS[invoiceType as InvoiceType] ??
      DEFAULT_VISIT_BILLED_STATUS,
    // ECRH-5881: the backend filters the list by invoice type, so only the
    // visits that can still carry an invoice of the selected type are offered
    // (Self Pay shows Self Pay visits, Out of Network shows OON visits, and PR
    // shows the insurance visits with a posted ERA/EOB). It also drops visits
    // that already have an invoice of that type, so the same visit cannot be
    // invoiced twice.
    invoiceType: invoiceType || undefined,
    // ECRH-5347: pass the typed search string so the visit can be found by its
    // Visit ID (backend filters on this key).
    searchKey: visitSearchKey || undefined,
    // Only the new-invoice flow shows the dropdown, so skip the fetch elsewhere.
    disabled: !selectedPatientId || !showSelectVisit,
  });

  // Claims of the selected visit whose ERA/EOB posting is complete. The backend
  // scopes the list to the patient, to that visit and to fully posted claims
  // carrying a patient responsibility, so partially posted / unposted / denied
  // claims, another visit's claims and another patient's claims can never reach
  // the dropdown.
  const {
    data: prEligibleClaims,
    isLoading: isLoadingPrClaims,
    isFetching: isFetchingPrClaims,
  } = useGetPrEligibleClaims({
    patientUuid: selectedPatientId ? String(selectedPatientId) : undefined,
    appointmentUuid: selectedVisitId ? String(selectedVisitId) : undefined,
    enabled: showSelectClaim,
  });

  // The claim id travels as the option value and is what gets linked to the
  // invoice on save; the user sees it in the same shortened form as the Claim ID
  // column on the Claims listing. Claims are de-duplicated on id so the same
  // claim can never be listed twice.
  const prClaimOptions = useMemo(() => {
    const seenClaimIds = new Set<string>();
    const options: { value: string; label: string }[] = [];
    (prEligibleClaims?.data ?? []).forEach((claim: any) => {
      const claimId = claim?.claimId ?? claim?.claimUuid;
      if (!claimId || seenClaimIds.has(claimId)) return;
      seenClaimIds.add(claimId);
      options.push({ value: claimId, label: getFormattedId(claimId) });
    });
    return options;
  }, [prEligibleClaims?.data]);

  const isLoadingClaimOptions = isLoadingPrClaims || isFetchingPrClaims;

  // The visit carries exactly one claim in the usual case, so pre-select it —
  // the user only has to choose when the visit was billed on several claims.
  useEffect(() => {
    if (!showSelectClaim || !selectedVisitId || isLoadingClaimOptions) return;
    if (patientValidationFormik?.values?.claim) return;
    if (prClaimOptions.length !== 1) return;
    patientValidationFormik?.setFieldValue("claim", prClaimOptions[0]);
  }, [
    showSelectClaim,
    selectedVisitId,
    isLoadingClaimOptions,
    prClaimOptions,
    patientValidationFormik?.values?.claim,
  ]);

  // Message shown in place of options once the fetch has answered and the
  // selected visit turned out to have no claim with a fully posted ERA/EOB.
  const hasNoEligibleClaims =
    !!selectedVisitId &&
    !isLoadingClaimOptions &&
    !!prEligibleClaims &&
    prClaimOptions.length === 0;

  // Clearing the patient must also clear the visit and the claim selected for
  // that patient, so another patient's invoice can't inherit them.
  const clearPatientAndVisit = (patientValue: string | null | undefined) => {
    patientValidationFormik?.setFieldValue("patient", patientValue);
    patientValidationFormik?.setFieldValue("encounter", undefined);
    patientValidationFormik?.setFieldValue("claim", undefined);
  };

  return (
    <Flex
      gap={2}
      minWidth={"450px"}
      flexWrap={"wrap"}
      flexDirection={"row"}
      justifyContent={"space-between"}
      px={0.5}
    >
      {/* detail about Bill  */}
      <Grid container width={"100%"}>
        <Flex
          gap={2}
          minWidth={"200px"}
          flexWrap={"wrap"}
          flexDirection={"row"}
          // justifyContent={"space-between"}
          alignItems={"center"}
        >
          <ArrowBackIcon
            color="primary"
            sx={{
              cursor: "pointer",
            }}
            fontSize="medium"
            onClick={() => {
              // When launched from Patient Charting → Financial, the origin
              // page is passed in router state; Back returns there instead of
              // the Billing area. Billing-section flows have no such state.
              const fromPatientChart = (location.state as any)?.from;
              if (fromPatientChart) {
                navigate(fromPatientChart);
                return;
              }
              // Back means nothing was committed, so return to the tab the flow
              // was launched from:
              //   invoice/:patientId/:encounterId  -> Ready For Billing
              //   create-pr-invoice/:patientId/:claimId -> Claim
              //   add / update / view-invoice      -> Invoice
              const invoiceRouteSegment = isUpdateRoute.split("/")[4];
              const backPath =
                invoiceRouteSegment === "invoice"
                  ? PROVIDER_PATHS.BILLING
                  : invoiceRouteSegment === "create-pr-invoice"
                    ? PROVIDER_PATHS.CLAIMS
                    : PROVIDER_PATHS.SUPER_BILL;
              navigate(backPath);
              // localStorage.setItem("billingSelectedTab", "super_bill");
            }}
          />
          <Text fontWeight={"bold"} variant="h6" fontSize={"20px"}>
            {title}
          </Text>
        </Flex>
        <Grid
          item
          // PR shows a third field (Claim ID) on the same row, so the patient
          // block gives up some width to keep all three side by side.
          xs={showSelectClaim ? 2.5 : 4}
          sx={{
            overflowWrap: "break-word",
            color: "primary.main",
            marginLeft: showSelectClaim ? "16px" : "40px",
            overflow: "hidden",
          }}
        >
          {patientValidationFormik?.values?.patient ? (
            <Flex
              gap={0}
              onClick={() => {
                if (isUpdateRoute.split("/")[4] === "add-invoice") {
                  clearPatientAndVisit(undefined);
                }
              }}
              sx={{
                cursor:
                  isUpdateRoute.split("/")[4] === "add-invoice"
                    ? "pointer"
                    : "default",
              }}
              flexWrap={"nowrap"}
              flexDirection={"row"}
              alignItems={"center"}
            >
              <img src={DEFAULT_USER_SRC} alt="" width={"24px"} />
              <Text fontWeight={"bold"} variant="h6" fontSize={"20px"} ml={1}>
                {patientsData?.firstName} {patientsData?.lastName}
                {patientsData?.ecarehealthPatientId &&
                  `(${patientsData?.ecarehealthPatientId})`}
              </Text>
              {viewBill && (
                <GridCloseIcon
                  color="error"
                  sx={{
                    cursor: "pointer",
                  }}
                  fontSize="medium"
                  onClick={() => {
                    clearPatientAndVisit(null);
                  }}
                />
              )}
            </Flex>
          ) : (
            <Box>
              <CustomSelectScroll
                required
                name="patient"
                placeholder="Select Patient"
                loading={isFetchingPatients}
                pagination={patientPagination}
                setPagination={setPatientPagination}
                totalElements={patientList?.data?.page?.totalElements}
                renderCustomLabel={(option) => (
                  <Flex flexDirection={"column"}>
                    <Text>{option.label}</Text>
                    <Text>
                      {option?.birthDate ? formatDate(option?.birthDate) : ""}
                    </Text>
                  </Flex>
                )}
                options={patientList?.data?.content?.map((it) => ({
                  label: `${it.firstName} ${it.lastName}`,
                  birthDate: it?.birthDate,
                  value: it.uuid,
                }))}
                onSearch={(_, val) => {
                  setSearchKey(val);
                  setPatientPagination((p) => ({ ...p, page: 0 }));
                }}
                value={patientValidationFormik?.values?.patient}
                onChange={(value) => {
                  patientValidationFormik?.setFieldValue("patient", value);
                }}
                error={
                  patientValidationFormik?.touched?.patient &&
                  patientValidationFormik?.errors?.patient
                }
              />
            </Box>
          )}
        </Grid>

        {/* Select Visit - new invoice flow only (ECRH-5087). Lists the selected
            patient's completed visits and drives service-detail auto-fill. */}
        {showSelectVisit && (
          <Grid
            item
            xs={showSelectClaim ? 2 : 3}
            sx={{ marginLeft: showSelectClaim ? "16px" : "40px" }}
          >
            <AsyncSelectScroll
              // ECRH-5330: Visit optional by default.
              // ECRH-5347: required when an invoice type was selected up front.
              required={isVisitRequired}
              name="encounter"
              label="Visit ID"
              placeholder="Select Visit ID"
              // Search is server-side (searchKey); disable MUI's built-in
              // client filter so it doesn't drop server-returned visits whose
              // label (type - time) doesn't contain the typed Visit ID.
              enableFilterOption
              disabled={!selectedPatientId}
              loading={isLoadingEncounterList || isFetchingEncounterList}
              pagination={visitPagination}
              setPagination={setVisitPagination}
              totalElements={patientEncounterList?.data?.page?.totalElements}
              renderCustomLabel={(option) => (
                <Text>{replaceUnderScoreAddSpace(option.label)}</Text>
              )}
              options={
                patientEncounterList?.data?.content?.map((option: any) => {
                  const encounterDate =
                    option?.startTime ?? option?.encounterDate;
                  const time = encounterDate
                    ? formatDateTime(encounterDate)
                    : "-";
                  return {
                    value: option.uuid,
                    label: `${getFormattedId(option.uuid)} - ${
                      option?.type ? option?.type : ""
                    } - ${time}`,
                    startTime: encounterDate,
                    chiefComplaint: option?.chiefComplaint,
                    uuid: option.uuid,
                  };
                }) ?? []
              }
              value={patientValidationFormik?.values?.encounter as any}
              handleBlur={patientValidationFormik?.handleBlur}
              onSearch={(val) => {
                setVisitSearchKey(val);
                setVisitPagination((p) => ({ ...p, page: 0 }));
              }}
              onChange={(value) => {
                patientValidationFormik?.setFieldValue("encounter", value);
                // The claim belongs to the visit it was billed on, so a new
                // visit invalidates whatever claim was picked for the old one.
                patientValidationFormik?.setFieldValue("claim", undefined);
              }}
              error={
                patientValidationFormik?.touched?.encounter &&
                patientValidationFormik?.errors?.encounter
              }
            />
          </Grid>
        )}

        {/* Select Claim - Patient Responsibility invoices only. Disabled until a
            visit is picked, then lists that visit's claims whose ERA/EOB posting
            is complete so the invoice can be linked back to (and pre-filled
            from) the claim it settles. */}
        {showSelectClaim && (
          <Grid item xs={2} sx={{ marginLeft: "16px" }}>
            <AsyncSelect
              required
              name="claim"
              label="Claim ID"
              placeholder="Select Claim ID"
              disabled={!selectedPatientId || !selectedVisitId}
              loading={isLoadingClaimOptions}
              options={prClaimOptions}
              value={patientValidationFormik?.values?.claim}
              handleBlur={patientValidationFormik?.handleBlur}
              onChange={(value) => {
                patientValidationFormik?.setFieldValue("claim", value);
              }}
              // The empty state rides on the field's own helper line instead of
              // a second line of its own, so the header stays one row high. It
              // replaces "Claim is required" because it is the reason the claim
              // cannot be filled in.
              error={
                hasNoEligibleClaims
                  ? "No eligible claims available."
                  : patientValidationFormik?.touched?.claim &&
                    patientValidationFormik?.errors?.claim
              }
            />
          </Grid>
        )}

        {/* remove as per BA requirement https://medarch.atlassian.net/browse/ECRH-3778 */}
        {/* {patientId && (
          <CommonBillDetails
            data={[
              {
                icon: <EventIcon color="primary" />,
                label: patientsData?.birthDate
                  ? `${formatDate(patientsData.birthDate) ?? ""} ${
                      patientsData.age
                        ? `(${patientsData?.age?.toString()}  Yrs)`
                        : ""
                    }
           (${patientsData.gender?.slice(0, 1)})`
                  : "-",
              },
              {
                icon: <PersonOutlineIcon color="primary" />,
                label: (
                  <RenderWithTooltip

                    value={getValueFromObj(patientsData?.providerId)}
                  />
                ),
              },
              {
                icon: <LocationOnOutlined color="primary" />,
                label: (
                  <RenderWithTooltip

                    value={getAddressFormat(patientsData?.address)}
                  />
                ),
              },
            ]}
          />
        )} */}
      </Grid>
    </Flex>
  );
};

export default SuperBillHeader;
